import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import {
  ImportZiel,
  ListenErkennung,
  ListenTypInfo,
  ProjektListenTyp,
  SpsImportErgebnis,
  SpsImportVorschau,
  SpsSammelImportDatei,
  SpsSammelImportErgebnis,
  istSpsListenTyp,
  listenTypInfo
} from '../Models/Sps';
import { SpsImportApiService } from './sps-import-api.service';

/**
 * Schlanke Koordination des gemeinsamen Imports für alle vier Listenarten.
 *
 *   Messstellenliste, Verbraucherliste  → bestehender KI-Import
 *                                          (`/wartung/inbetriebnahme/import`, InbetriebnahmeService)
 *   SPS-Zuordnungsliste, PLC-Tag-Liste  → neuer SPS-Import (SpsImportApiService)
 *
 * Der Dienst entscheidet nur, welcher Weg zuständig ist, und reicht SPS-Aufrufe
 * durch. Er ändert nichts am bestehenden Importablauf und startet nie von
 * selbst einen Import – das tun später die Komponenten nach Bestätigung.
 */
@Injectable({ providedIn: 'root' })
export class ListenImportKoordinatorService {

  /** Bestehende Importseite für Messstellen und Verbraucher. */
  static readonly INBETRIEBNAHME_IMPORT_ROUTE = '/wartung/inbetriebnahme/import';

  constructor(
    private spsImport: SpsImportApiService,
    private router: Router
  ) { }

  /** Listenart einer Datei erkennen (alle vier Typen). Speichert nichts. */
  erkenneDatei(file: File, kiVerwenden = false): Observable<ListenErkennung> {
    return this.spsImport.erkennen(file, kiVerwenden);
  }

  /** Welcher Importweg einen (bestätigten) Typ speichert – null bei 'Unbekannt'. */
  importZielFuer(typ: ProjektListenTyp): ImportZiel | null {
    if (typ === 'Unbekannt') {
      return null;
    }

    return istSpsListenTyp(typ) ? 'sps' : 'inbetriebnahme';
  }

  info(typ: ProjektListenTyp): ListenTypInfo {
    return listenTypInfo(typ);
  }

  /** Dateien eines Sammelimports nach Importweg trennen. */
  trenneNachImportZiel(dateien: SpsSammelImportDatei[]): {
    sps: SpsSammelImportDatei[];
    inbetriebnahme: SpsSammelImportDatei[];
    unbekannt: SpsSammelImportDatei[];
  } {
    return {
      sps: dateien.filter(d => this.importZielFuer(d.listenTyp) === 'sps'),
      inbetriebnahme: dateien.filter(d => this.importZielFuer(d.listenTyp) === 'inbetriebnahme'),
      unbekannt: dateien.filter(d => this.importZielFuer(d.listenTyp) === null)
    };
  }

  /** SPS-Vorschau nach bestätigtem Typ. Speichert nichts. */
  spsVorschau(
    file: File,
    listenTyp: ProjektListenTyp,
    optionen?: {
      sheetName?: string | null; maxZeilen?: number | null; projektId?: number | null; seite?: number;
      seitengroesse?: number;
    }
  ): Observable<SpsImportVorschau> {
    this.pruefeSpsTyp(listenTyp);
    return this.spsImport.vorschau(file, listenTyp, optionen);
  }

  /** Eine SPS-Liste nach Bestätigung importieren. */
  spsImportieren(
    file: File,
    projektId: number,
    listenTyp: ProjektListenTyp,
    optionen?: { sheetName?: string | null; name?: string | null }
  ): Observable<SpsImportErgebnis> {
    this.pruefeSpsTyp(listenTyp);
    return this.spsImport.importieren(file, projektId, listenTyp, optionen);
  }

  /**
   * Mehrere SPS-Listen nach Bestätigung importieren. Dateien anderer Typen
   * müssen vorher mit `trenneNachImportZiel` ausgesondert werden.
   */
  spsImportierenMehrfach(projektId: number, dateien: SpsSammelImportDatei[]): Observable<SpsSammelImportErgebnis> {
    const fremde = dateien.filter(d => !istSpsListenTyp(d.listenTyp));

    if (fremde.length > 0) {
      throw new Error(
        `Nur SPS-Listen können über den SPS-Sammelimport gespeichert werden (${fremde.map(f => f.file.name).join(', ')}).`);
    }

    return this.spsImport.importierenMehrfach(projektId, dateien);
  }

  /**
   * Zum bestehenden KI-Import für Messstellen-/Verbraucherlisten wechseln –
   * mit denselben Query-Parametern, die die Projektansicht bisher verwendet.
   * Die Datei muss der Benutzer dort erneut wählen; der bestehende Ablauf bleibt unverändert.
   */
  zumBestehendenImport(projektId: number, projektName?: string | null): Promise<boolean> {
    return this.router.navigate(
      [ListenImportKoordinatorService.INBETRIEBNAHME_IMPORT_ROUTE],
      { queryParams: { projektId, projektName: projektName ?? undefined } });
  }

  private pruefeSpsTyp(typ: ProjektListenTyp): void {
    if (!istSpsListenTyp(typ)) {
      throw new Error(`Listentyp ${typ} wird über den bestehenden Inbetriebnahme-Import gespeichert, nicht über den SPS-Import.`);
    }
  }
}
